import React, { useState, useEffect } from "react";
import "./biller.scss";
import { FaUsers, FaMobileAlt, FaGlobe, FaSearch, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Spin, message, Table } from "antd";
import { RiCodeSSlashLine, RiCodeSLine } from "react-icons/ri";
import { JSONTree } from "react-json-tree";
import actions from "../../../redux/billers/actions";

const BillerDetail = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [copyCount, setCopyCount] = useState(0);
  const [toggleBillerDetailsModal, setToggleBillerDetailsModal] = useState(true);
  const [selectedBillerData, setSelectedBillerData] = useState(null);
  const [showBillerCard, setShowBillerCard] = useState(false);
  const [showBillerTable, setShowBillerTable] = useState(false);
  const [showChannelTable, setShowChannelTable] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const [currentFilters, setCurrentFilters] = useState({
    category: "",
    channel: "",
  });

  const { totalBillersData = {}, searchBillersResult, loading, error } =
    useSelector((state) => state?.BillerReducer || {});
    
      const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
      });

  useEffect(() => {
    if (error) {
      messageApi.error(error);
      dispatch(actions.clearSearchBillers());
    }
  }, [error, messageApi]);

  useEffect(() => {
    dispatch(actions.getTotalBillers());
  }, [dispatch]);

  const handleSearch = (e) => {
    if (e.key === "Enter" && search.trim()) {
      if (/^[A-Z0-9]{14}$/.test(search.trim())) {
        // Biller ID search
        dispatch(actions.searchBillers({ billerId: search.trim() }));
        setShowBillerCard(true);
        setShowBillerTable(false);
        setShowChannelTable(false);
        setCurrentFilters({ category: "", channel: "" });
      } else {
        // Category search
        const category = search.trim();
        const channel = currentFilters.channel;
        if (category || channel) {
          dispatch(actions.searchBillers({ category, channel, page: 1, limit: 10 }));
        }
        setShowBillerCard(false);
        setShowBillerTable(true);
        setShowChannelTable(!!channel);
        setCurrentFilters({ category, channel });
      }
    }
  };

  const handleChannelClick = (channel) => {
    const category = currentFilters.category;
    const newChannel = selectedChannel === channel ? "" : channel; // toggle off if already selected
    setSelectedChannel(newChannel);
    setShowChannelTable(!!category || !!newChannel);
    setShowBillerTable(!!category);
    setShowBillerCard(false);
    setCurrentFilters({ category, channel: newChannel });

    if (category || newChannel) {
      dispatch(actions.searchBillers({ category, channel: newChannel, page: 1, limit: 10 }));
    }
  };

  const handleViewBillerDetails = (biller) => {
    setSelectedBillerData(biller);
    setToggleBillerDetailsModal(false);
  };

  const handleCopyClick = () => {
    if (copyCount < 3) {
      navigator.clipboard
        .writeText(JSON.stringify(selectedBillerData || {}))
        .then(() => {
          messageApi.success("Copied to clipboard!");
          setCopyCount(copyCount + 1);
        })
        .catch(() => messageApi.error("Failed to copy!"));
    }
  };

  const handleBillerDetailsModalClose = () => {
    setToggleBillerDetailsModal(true);
    setSelectedBillerData(null);
    setCopyCount(0);
  };

  const handleCloseBillerCard = () => {
    setShowBillerCard(false);
    setSearch("");
    setCurrentFilters({ category: "", channel: "" });
    dispatch(actions.clearSearchBillers());
  };

  const handleCloseChannelTable = () => {
    setShowChannelTable(false);
    setSelectedChannel("");
    setCurrentFilters({ ...currentFilters, channel: "" });
  };

  const handleClearSearch = () => {
    setSearch("");
    setCurrentFilters({ category: "", channel: selectedChannel });
    if (selectedChannel) {
      dispatch(actions.searchBillers({ channel: selectedChannel, page: 1, limit: 10 }));
    }
    setShowBillerCard(false);
    setShowBillerTable(!!selectedChannel);
    setShowChannelTable(!!selectedChannel);
  };

  const isChannelSelected = (channel) => selectedChannel === channel;

  const totalBillers = totalBillersData?.totalBillers || 0;
  const agtBillers = totalBillersData?.channelCounts?.AGT || 0;
  const mobBillers = totalBillersData?.channelCounts?.MOB || 0;
  const intBillers = totalBillersData?.channelCounts?.INT || 0;

 const columns = [
  {
    title: "#",
    key: "index",
    width: 60,
    render: (_, __, index) =>
      ((searchBillersResult?.page || 1) - 1) *
        (searchBillersResult?.limit || 10) +
      index +
      1, // ✅ continuous numbering
  },
  { title: "Biller ID", dataIndex: "billerId", key: "billerId" },
  { title: "Biller Name", dataIndex: "billerName", key: "billerName" },
  { title: "Category", dataIndex: "billerCategoryName", key: "billerCategoryName" },
  {
    title: "View",
    key: "view",
    render: (_, record) => (
      <Button type="link" onClick={() => handleViewBillerDetails(record)}>
        View
      </Button>
    ),
  },
];


  const tableData =
    Array.isArray(searchBillersResult?.data) &&
    searchBillersResult?.data?.map((biller, index) => ({ key: index + 1, ...biller }));

  return (
    <div className="biller-container">
      {contextHolder}

      {/* Header */}
      <div className="biller-header">
        <h1 style={{ fontSize: "50px" }}>Biller Dashboard</h1>
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by Biller ID, Category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearch}
          />
          {search && (
            <FaTimes
              className="search-clear-icon"
              onClick={handleClearSearch}
              style={{ position: "absolute", right: "10px", top: "10px", cursor: "pointer" }}
            />
          )}
        </div>
      </div>

      {/* Single biller card */}
      {showBillerCard &&
        searchBillersResult &&
        searchBillersResult?.data &&
        !Array.isArray(searchBillersResult?.data) && (
          <div className="biller-detail-card">
            <div className="close-btn" onClick={handleCloseBillerCard}>
              <FaTimes size={20} />
            </div>
            <div className="biller-info">
              <h2>{searchBillersResult.data.billerName}</h2>
              <p>ID: {searchBillersResult.data.billerId}</p>
              <p>Category: {searchBillersResult.data.billerCategoryName}</p>
              <p>Status: {searchBillersResult.data.Status}</p>
            </div>
            <div className="view-details-btn" onClick={() => handleViewBillerDetails(searchBillersResult.data)}>
              {toggleBillerDetailsModal === false &&
              selectedBillerData?.billerId === searchBillersResult.data.billerId ? (
                <RiCodeSLine size={25} color="#073763" />
              ) : (
                <RiCodeSSlashLine size={25} />
              )}
              <span>View Full Details</span>
            </div>
          </div>
        )}

      {/* Total Billers */}
      <div className="card total-card">
        <h1>Total Billers</h1>
        {loading ? <Spin className="custom-spin" /> : <p>{totalBillers}</p>}
      </div>

      {/* Channel cards */}
      <div className="grid">
        <div
          className={`card agt ${isChannelSelected("AGT") ? "selected" : ""}`}
          onClick={() => handleChannelClick("AGT")}
          style={{ cursor: "pointer" }}
        >
          <FaUsers className="icon agt-icon" />
          <h3>AGT Billers</h3>
          {loading ? <Spin className="custom-spin-channel" /> : <p>{agtBillers}</p>}
        </div>
        <div
          className={`card mob ${isChannelSelected("MOB") ? "selected" : ""}`}
          onClick={() => handleChannelClick("MOB")}
          style={{ cursor: "pointer" }}
        >
          <FaMobileAlt className="icon mob-icon" />
          <h3>MOB Billers</h3>
          {loading ? <Spin className="custom-spin-channel" /> : <p>{mobBillers}</p>}
        </div>
        <div
          className={`card int ${isChannelSelected("INT") ? "selected" : ""}`}
          onClick={() => handleChannelClick("INT")}
          style={{ cursor: "pointer" }}
        >
          <FaGlobe className="icon int-icon" />
          <h3>INT Billers</h3>
          {loading ? <Spin className="custom-spin-channel" /> : <p>{intBillers}</p>}
        </div>
      </div>

      {/* Table for category or channel results */}
      {(showBillerTable || showChannelTable) && Array.isArray(searchBillersResult?.data) && (
        <div style={{ marginTop: "30px", position: "relative" }}>
          {showChannelTable && (
            <div
              className="close-btn"
              onClick={handleCloseChannelTable}
              style={{ position: "absolute", right: 0, top: -1, cursor: "pointer" }}
            >
              <FaTimes size={20} />
            </div>

          )}
          <Table
            columns={columns}
            dataSource={tableData || []}
            loading={loading}
            bordered
            rowKey="billerId"
            pagination={{
              current: searchBillersResult?.page || 1,
              pageSize: searchBillersResult?.limit || 10,
              total: searchBillersResult?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, limit) => {
                if (currentFilters.category || currentFilters.channel) {
                  dispatch(actions.searchBillers({ ...currentFilters, page, limit }));
                }
              },
            }}
          />
        </div>
      )}

      {/* Modal for full biller details */}
      <Modal
        title="Full Biller Details"
        open={!toggleBillerDetailsModal}
        onOk={handleBillerDetailsModalClose}
        onCancel={handleBillerDetailsModalClose}
        width={700}
        centered
        maskClosable={false}
        footer={[
          <Button key="back" onClick={handleBillerDetailsModalClose}>
            OK
          </Button>,
        ]}
      >
        <div className="py-2" style={{ position: "relative" }}>
          <Button
            key="copy"
            style={{ position: "absolute", top: "20px", zIndex: 10, right: "30px" }}
            onClick={handleCopyClick}
            disabled={copyCount >= 3}
          >
            Copy
          </Button>
          <div
            className="bg-white border rounded border-black code-modal-div"
            style={{ padding: "10px", height: "480px", overflowY: "auto", borderRadius: "10px" }}
          >
            {loading ? (
              <span style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "480px" }}>
                <Spin />
              </span>
            ) : (
              <JSONTree
                data={selectedBillerData || {}}
                theme={"bright:inverted"}
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BillerDetail;
